// <purpose>The mail's files at the top of compare mode's mail column (2026-10-30): one split chip per
// file — clip, the name cut in the middle, the size │ the OCR state word — documents first, inline
// images after a gap when the tenant counts them, else the muted inline line. A chip opens a small
// card with the full name, the size and who decided the OCR and why. More than two lines collapse
// into „+n weitere", which opens the rest as a list rather than pushing the mail down.</purpose>
// <dependencies>services/attachmentOcrStatus (rows, labels, counting, shortenFileName), theme.</dependencies>
// <constraints>The chip row never grows after first paint: the overflow is measured once per row
// set and the mail text below it never moves. Decorative glyphs: none (SVG clip only).</constraints>
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PaperclipIcon } from './shared';
import { theme } from '../theme';
import {
  OCR_DECIDER_LABELS, OCR_STATE_LABELS, formatBytes, inlineSummaryLabel, ocrReasonLabel, shortenFileName,
  type AttachmentOcrRow,
} from '../services/attachmentOcrStatus';

const CHIP_HEIGHT = 22;
const ROW_GAP = 6;

const stateColor = (row: AttachmentOcrRow): string =>
  row.state === 'read' ? theme.colors.success : row.state === 'not_read' ? theme.colors.navyMuted : theme.colors.textSecondary;

const chipTitle = (row: AttachmentOcrRow): string => {
  if (row.state === 'unknown') return `${row.name} — keine OCR-Entscheidung protokolliert`;
  const decider = row.decidedBy ? ` · ${OCR_DECIDER_LABELS[row.decidedBy]}` : '';
  const reason = ocrReasonLabel(row.reason);
  return `${row.name} — OCR ${OCR_STATE_LABELS[row.state]}${decider}${reason ? ` — ${reason}` : ''}`;
};

const cardBox: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 5, width: 280, boxSizing: 'border-box',
  backgroundColor: theme.colors.white, border: `1px solid ${theme.colors.borderLight}`, borderRadius: 10,
  boxShadow: '0 8px 24px rgba(0,13,102,0.14)', padding: '10px 12px', fontSize: 12, lineHeight: 1.5,
  color: theme.colors.navy, fontFamily: theme.font.family, animation: 'stx-cardRise 0.15s ease-out',
};

const Chip = React.forwardRef<HTMLButtonElement, { row: AttachmentOcrRow; open: boolean; onToggle: () => void; hidden?: boolean }>(({ row, open, onToggle, hidden }, ref) => (
  <span style={{ position: 'relative', display: hidden ? 'none' : 'inline-flex' }}>
    <button
      ref={ref}
      type="button"
      data-testid="attachment-chip"
      onClick={onToggle}
      title={chipTitle(row)}
      aria-expanded={open}
      style={{
        display: 'inline-flex', alignItems: 'stretch', height: CHIP_HEIGHT, padding: 0, boxSizing: 'border-box',
        border: `1px solid ${open ? theme.colors.navy : theme.colors.borderLight}`, borderRadius: 999, overflow: 'hidden',
        backgroundColor: theme.colors.white, cursor: 'pointer', fontFamily: theme.font.family, whiteSpace: 'nowrap',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 8px', backgroundColor: theme.colors.navyTint, borderRight: `1px solid ${theme.colors.borderLight}`, maxWidth: 260 }}>
        <PaperclipIcon size={12} color={theme.colors.navyMid} />
        <span style={{ fontSize: 11.5, color: theme.colors.navyMid, overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortenFileName(row.name)}</span>
        <span style={{ fontSize: 11, color: theme.colors.textSecondary }}>{formatBytes(row.size)}</span>
      </span>
      <span data-testid="attachment-chip-state" style={{ display: 'inline-flex', alignItems: 'center', padding: '0 8px', fontSize: 11, fontWeight: 600, color: stateColor(row) }}>
        {OCR_STATE_LABELS[row.state]}
      </span>
    </button>
    {open && (
      <div role="dialog" aria-label={row.name} data-testid="attachment-card" style={cardBox}>
        <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>{row.name}</div>
        <div style={{ color: theme.colors.textSecondary }}>Größe {formatBytes(row.size)}</div>
        <div>
          {row.state === 'unknown'
            ? 'Keine OCR-Entscheidung protokolliert'
            : `OCR ${OCR_STATE_LABELS[row.state]}${row.decidedBy ? ` · ${OCR_DECIDER_LABELS[row.decidedBy]}` : ''}${ocrReasonLabel(row.reason) ? ` — ${ocrReasonLabel(row.reason)}` : ''}`}
        </div>
      </div>
    )}
  </span>
));
Chip.displayName = 'AttachmentChip';

export const AttachmentChips: React.FC<{
  documents: AttachmentOcrRow[];
  inline: AttachmentOcrRow[];
  countInline: boolean;
  /** The ticket's attachment digest exists: a link scrolls the mail column to its box. */
  onSummary?: () => void;
  /** Bumps when the count segment opened compare mode: focus the first chip. */
  focusSeq?: number;
}> = ({ documents, inline, countInline, onSummary, focusSeq }) => {
  const rows = countInline ? [...documents, ...inline] : documents;
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [overflowFrom, setOverflowFrom] = useState<number | null>(null);
  const [restOpen, setRestOpen] = useState(false);
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const signature = rows.map(r => r.name).join('|');
  // Two lines at most: chips whose top lies below the second line go behind „+n weitere".
  useLayoutEffect(() => {
    setOverflowFrom(null);
  }, [signature]);
  useLayoutEffect(() => {
    if (overflowFrom !== null) return;
    const tops = chipRefs.current.slice(0, rows.length).map(el => el?.offsetTop ?? 0);
    if (!tops.length) return;
    const first = Math.min(...tops);
    const secondLine = first + CHIP_HEIGHT + ROW_GAP;
    const over = tops.findIndex(t => t > secondLine + 1);
    // Leave room for the „+n weitere" chip on the last line.
    if (over > 0) setOverflowFrom(Math.max(1, over - 1));
  }, [overflowFrom, signature, rows.length]);
  useEffect(() => {
    if (!focusSeq) return;
    chipRefs.current[0]?.focus({ preventScroll: true });
  }, [focusSeq]);
  useEffect(() => {
    if (openIdx === null && !restOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation(); setOpenIdx(null); setRestOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [openIdx, restOpen]);

  const uncountedInline = !countInline ? inline.length : 0;
  if (rows.length === 0 && uncountedInline === 0 && !onSummary) return null;
  const rest = overflowFrom !== null ? rows.slice(overflowFrom) : [];
  return (
    <div data-testid="attachment-chips" style={{
      display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 8, marginBottom: 10,
      borderBottom: `1px solid ${theme.colors.bgLight}`,
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: ROW_GAP, alignItems: 'center' }}>
        {rows.map((row, i) => (
          <React.Fragment key={`${row.name}#${i}`}>
            {/* The inline images follow the documents after a wider gap. */}
            {countInline && i === documents.length && i > 0 && <span aria-hidden="true" style={{ width: 4 }} />}
            <Chip
              ref={el => { chipRefs.current[i] = el; }}
              row={row}
              open={openIdx === i}
              hidden={overflowFrom !== null && i >= overflowFrom}
              onToggle={() => setOpenIdx(o => (o === i ? null : i))}
            />
          </React.Fragment>
        ))}
        {rest.length > 0 && (
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <button type="button" data-testid="attachment-chips-more" aria-expanded={restOpen} onClick={() => setRestOpen(o => !o)} style={{
              height: CHIP_HEIGHT, padding: '0 10px', borderRadius: 999, border: `1px solid ${theme.colors.borderLight}`,
              backgroundColor: theme.colors.white, fontFamily: theme.font.family, fontSize: 11.5, fontWeight: 600, color: theme.colors.navyMid, cursor: 'pointer',
            }}>+{rest.length} weitere</button>
            {restOpen && (
              <div role="dialog" aria-label="Weitere Anhänge" style={{ ...cardBox, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {rest.map((row, i) => (
                  <div key={`${row.name}#${i}`} title={chipTitle(row)} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{row.name}</span>
                    <span style={{ fontSize: 11, color: theme.colors.textSecondary, flexShrink: 0 }}>{formatBytes(row.size)}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: stateColor(row), flexShrink: 0 }}>{OCR_STATE_LABELS[row.state]}</span>
                  </div>
                ))}
              </div>
            )}
          </span>
        )}
        {onSummary && (
          <button type="button" data-testid="attachment-summary-link" onClick={onSummary} style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: theme.font.family,
            fontSize: 11.5, color: theme.colors.navy, textDecoration: 'underline', textDecorationStyle: 'dotted',
          }}>Zusammenfassung</button>
        )}
      </div>
      {uncountedInline > 0 && (
        <div data-testid="anhang-inline-summary" style={{ fontSize: 11, color: theme.colors.textSecondary }}>{inlineSummaryLabel(uncountedInline)}</div>
      )}
    </div>
  );
};

export default AttachmentChips;
